import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Any path starting with one of these prefixes is treated as an authenticated
// studio / portal route. Everything else (marketing pages, /login, /blog, etc.)
// is a public route.
const AUTHED_PREFIXES = [
  "/portal",
  "/board",
  "/team",
  "/requests",
  "/invoices",
  "/attendance",
  "/chat",
  "/glance",
  "/site-visits",
  "/marketing-admin",
];

function isAuthedPath(pathname) {
  return AUTHED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
  );
}

/**
 * SessionGuard — when an authenticated user hits the browser Back / Forward
 * button and lands on a public / marketing route, we automatically sign them
 * out. Combined with the backend's session-scoped auth cookie (no max_age),
 * this guarantees closing the tab, browser, OR navigating back to the public
 * site all invalidate the session.
 *
 * NOTE: We only react to `POP` navigations. Regular in-app clicks are `PUSH`
 * and are ignored, so the "View Public Site" link in the sidebar keeps working.
 */
export default function SessionGuard() {
  const location = useLocation();
  const navType = useNavigationType();
  const { user, logout } = useAuth();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const now = location.pathname;
    const prev = prevPathRef.current;
    const isAuthed = user && user !== false;

    if (
      navType === "POP" &&
      isAuthed &&
      isAuthedPath(prev) &&
      !isAuthedPath(now)
    ) {
      logout();
    }

    prevPathRef.current = now;
  }, [location.pathname, navType, user, logout]);

  return null;
}
